import React from 'react';

/**
 * LustLink Seal — the official brand mark.
 *
 * Circular emblem with a gilded LL ligature centered, outer royal-blue ring,
 * inner navy field, "EST · 2026 · PRIVATE" curved along the top arc, small
 * decorative gold dot at the bottom.
 *
 * Renders as an inline SVG so it scales crisply from 20px favicon to 512px
 * splash and inherits its stroke/fill colors from props.
 *
 * Props:
 *   size (number | string, default 40) — width/height in px
 *   showArcText (bool, default true) — hide the "EST · 2026 · PRIVATE" arc at tiny sizes
 *   className (string) — passthrough for layout
 */
const LustLinkSeal = ({ size = 40, showArcText = true, className = '' }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 110 110"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label="LustLink"
      data-testid="lustlink-seal"
    >
      {/* Outer royal-blue ring */}
      <circle cx="55" cy="55" r="52" fill="none" stroke="#2563eb" strokeWidth="2" />
      {/* Inner navy field */}
      <circle cx="55" cy="55" r="46" fill="#0b1740" />
      {/* Faint gold inner ring */}
      <circle cx="55" cy="55" r="46" fill="none" stroke="#fbbf24" strokeWidth="1" opacity="0.55" />

      {/* Radiating tick marks (compass points) */}
      <g stroke="#2563eb" strokeWidth="1" opacity="0.4">
        <line x1="55" y1="10" x2="55" y2="18" />
        <line x1="55" y1="92" x2="55" y2="100" />
        <line x1="10" y1="55" x2="18" y2="55" />
        <line x1="92" y1="55" x2="100" y2="55" />
        <line x1="23" y1="23" x2="29" y2="29" />
        <line x1="87" y1="23" x2="81" y2="29" />
        <line x1="23" y1="87" x2="29" y2="81" />
        <line x1="87" y1="87" x2="81" y2="81" />
      </g>

      {/* Center LL ligature — gilded */}
      <text
        x="55"
        y="66"
        textAnchor="middle"
        fontFamily="'Playfair Display', Georgia, serif"
        fontSize="42"
        fontWeight="900"
        fill="#fbbf24"
      >
        LL
      </text>

      {/* Curved top-arc microtype (only at legible sizes) */}
      {showArcText && (
        <>
          <defs>
            <path id="lustlink-seal-arc" d="M 20 55 A 35 35 0 0 1 90 55" fill="none" />
          </defs>
          <text
            fontFamily="'Inter', system-ui, sans-serif"
            fontSize="6.5"
            fontWeight="700"
            fill="#93c5fd"
            letterSpacing="3"
          >
            <textPath href="#lustlink-seal-arc" startOffset="50%" textAnchor="middle">
              EST · 2026 · PRIVATE
            </textPath>
          </text>
        </>
      )}

      {/* Bottom decorative dot */}
      <circle cx="55" cy="88" r="1.5" fill="#fbbf24" />
    </svg>
  );
};

export default LustLinkSeal;
